import { usePWA } from "@/contexts/PWAContext";
import { IOS_LIQUID_GLASS_VERSION } from "@/utils/enum";
import IosShareIcon from '@mui/icons-material/IosShare';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import ChromeIcon from "../icons/chromeIcon";
import SafariIcon from "../icons/safariIcon";
import { Description, Title } from "./AndroidChormeGuide";
import { desBrowserSx, desBrowserTextSx, dialogPaperSx } from "./ButtonInstallPWA.styles";
import StepCard, { StepGuide } from "./StepCard";

export interface Props {
  openModal: boolean;
  handleClose: () => void;
  handleInstall: () => void;
}

const DefaultSteps: Partial<StepGuide>[] = [
  {
    img: "3.png",
    textLines: ["「ホーム画面に", "追加」をタップ"],
    icon: null,
  },
  {
    img: "4.png",
    textLines: ["「追加」をクリック", "してインストールします"],
    icon: null,
  }
]

const ChromeSteps: Partial<StepGuide>[] = [
  {
    img: "1.png",
    textLines: ["をクリック", "の隣に Chromeの検索バー"],
    icon: <IosShareIcon sx={{ fontSize: "18px" }} />,
  }
];

const SafariSteps: Partial<StepGuide>[] = [
  {
    img: "1.png",
    textLines: ["をクリック", "下に Safariの検索バーの"],
    icon: <IosShareIcon sx={{ fontSize: "18px" }} />,
  }
];

const SafariLiquidGlassSteps: Partial<StepGuide>[] = [
  {
    img: "liquid-glass-1.png",
    textLines: [
      "をクリック",
      "隣に Safariの検索バー",
    ],
    icon: <IosShareIcon sx={{ fontSize: "18px" }} />,
  },
  {
    img: "liquid-glass-2.png",
    textLines: ["をクリック", "「共有」を メニューで"],
    icon: null,
  }
];


export default function IosGuideModal({
  openModal,
  handleClose,
}: Props) {
  const { deviceInfo } = usePWA();
  const { isChrome, browserName, majorVersion } = deviceInfo;

  const BrowserIcon = isChrome ? ChromeIcon : SafariIcon;

  const firstSteps = isChrome
    ? ChromeSteps
    : Number(majorVersion) >= IOS_LIQUID_GLASS_VERSION ? SafariLiquidGlassSteps : SafariSteps;

  const steps = [...firstSteps, ...DefaultSteps];

  return (
    <Dialog
      open={openModal}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Title handleClose={handleClose} />
      <DialogContent sx={{ pt: 0, pb: 4, px: 2, }}>
        <Stack direction="column" spacing={2}>
          {/* Description */}
          <Description />

          <Divider sx={{ mb: 2.5 }} />

          <Box sx={desBrowserSx}>
            <BrowserIcon sx={{ fontSize: "14px" }} />
            <Typography sx={{ ...desBrowserTextSx }}>
              {browserName} からアプリをインストール
            </Typography>
          </Box>

          {/* Steps grid */}
          <Grid container columns={12} justifyContent="center">
            {steps.map((s, idx) => {
              return (
                <Grid item xs={6} key={idx + 1}>
                  <StepCard
                    key={idx}
                    badge={idx + 1}
                    img={`/images/${browserName}-step-${s.img}`}
                    icon={s.icon}
                    textLines={s?.textLines || []}
                  />
                </Grid>
              )
            })}
          </Grid>

          {/* Bottom CTA */}
          <Button
            fullWidth
            size="large"
            onClick={handleClose}
            variant="outlined"
            color="primary"
          >
            完了
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
